import { FlexColumn, StyleUtils } from '@main/core-ui';
import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '../../Button';
import { CODE_FONT_STYLE } from '../../CodeBlock/CodeBlock';
import { Input } from '../../Input';
import { customFunctionMessages } from '../messages';
import {
  CustomFunctionActionType,
  type CustomFunctionStateAction,
} from '../state';
import type { EnvironmentVariable } from '../types';
import { DescriptionWrapper } from '../wrappers';
import { Environment } from './shared';

interface EnvironmentVariablesTabProps {
  /** The environment variables that already exist */
  value: EnvironmentVariable[];
  /** The dispatch function to handle changes in environment variables */
  dispatch: React.Dispatch<CustomFunctionStateAction>;
}

export const EnvironmentVariablesTab: React.FC<
  EnvironmentVariablesTabProps
> = ({ value, dispatch }) => {
  const { formatMessage } = useIntl();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, [value]);

  return (
    <>
      <DescriptionWrapper>
        {formatMessage(
          customFunctionMessages.environmentVariablesInstructions,
          {
            Environment,
          },
        )}
      </DescriptionWrapper>
      <FlexColumn
        ref={scrollContainerRef}
        style={{
          gap: StyleUtils.Spacing.sm,
          height: '100%',
          overflowY: 'auto',
          padding: `${StyleUtils.Spacing.sm} 0`,
        }}
      >
        {value.map((envVar, index) => (
          <div
            key={index}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr auto auto',
              gap: '0.5rem',
              alignItems: 'center',
            }}
          >
            <div>
              <label htmlFor={`env-name-${index}`} className="sr-only">
                {formatMessage(customFunctionMessages.environmentVariableName)}
              </label>
              <Input
                id={`env-name-${index}`}
                placeholder="Variable name"
                value={envVar.name}
                onChange={(e) =>
                  dispatch({
                    type: CustomFunctionActionType.UPDATE_ENV_VAR,
                    index,
                    field: 'name',
                    value: e.target.value,
                  })
                }
                style={{
                  ...CODE_FONT_STYLE,
                }}
              />
            </div>
            <div className="relative">
              <label htmlFor={`env-value-${index}`} className="sr-only">
                {formatMessage(customFunctionMessages.environmentVariableValue)}
              </label>
              <Input
                id={`env-value-${index}`}
                placeholder="Value"
                type={envVar.isSecret ? 'password' : 'text'}
                value={envVar.value}
                onChange={(e) =>
                  dispatch({
                    type: CustomFunctionActionType.UPDATE_ENV_VAR,
                    index,
                    field: 'value',
                    value: e.target.value,
                  })
                }
                style={{
                  ...CODE_FONT_STYLE,
                }}
              />
            </div>
            <Button
              variant="outline-secondary"
              noBorder
              icon={envVar.isSecret ? 'eye' : 'eye-slash'}
              iconOnly
              onClick={() =>
                dispatch({
                  type: CustomFunctionActionType.TOGGLE_VISIBILITY_ENV_VAR,
                  index,
                })
              }
              title={envVar.isSecret ? 'Show value' : 'Hide value'}
              style={{
                padding: 0,
              }}
            />
            <Button
              variant="outline-secondary"
              noBorder
              icon="trash"
              iconOnly
              onClick={() =>
                dispatch({
                  type: CustomFunctionActionType.REMOVE_ENV_VAR,
                  index,
                })
              }
              title="Remove variable"
              style={{
                padding: 0,
              }}
            />
          </div>
        ))}
      </FlexColumn>
      <Button
        icon="plus"
        variant="outline-secondary"
        onClick={() => dispatch({ type: CustomFunctionActionType.ADD_ENV_VAR })}
      >
        {formatMessage(customFunctionMessages.addEnvironmentVariable)}
      </Button>
    </>
  );
};
